import React, { useState } from "react";
import DOMPurify from "dompurify";
import blogsAPI from "../services/BlogsAPI";
import "./css/BlogGenerator.css";
import { formatDate } from "../utils/DateUtil";
import { formatDateTime, formatTimeOnly } from "../utils/DateTimeUtil";
import LoadingSpinner from "../common/LoadingSpinner";

/**
 * BlogGenerator component for generating blog posts from URLs
 * This component allows platform administrators to generate blog posts
 * from URLs using AI-powered content generation
 */
const BlogGenerator = () => {
  const [urls, setUrls] = useState([""]);
  const [wordCount, setWordCount] = useState(3000);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);

  /**
   * Add a new URL input field
   */
  const addUrlField = () => {
    setUrls([...urls, ""]);
  };

  /**
   * Remove a URL input field at the specified index
   */
  const removeUrlField = (index) => {
    if (urls.length > 1) {
      const newUrls = urls.filter((_, i) => i !== index);
      setUrls(newUrls);
    }
  };

  /**
   * Update URL at specific index
   */
  const updateUrl = (index, value) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  /**
   * Handle form submission to generate blog post
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty URLs
    const validUrls = urls.filter((url) => url.trim());

    // Validate URLs
    if (validUrls.length === 0) {
      setError("Please enter at least one valid URL");
      return;
    }

    // Basic URL validation for all URLs
    const invalidUrls = [];
    for (let i = 0; i < validUrls.length; i++) {
      try {
        new URL(validUrls[i]);
      } catch {
        invalidUrls.push(`URL ${i + 1}: ${validUrls[i]}`);
      }
    }

    if (invalidUrls.length > 0) {
      setError(
        `Please enter valid URL formats for the following:\n${invalidUrls.join(
          "\n",
        )}`,
      );
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setProgress(null);

    try {
      console.log("Generating blog from URLs:", validUrls);

      // For multiple URLs, we'll use the new API endpoint
      if (validUrls.length > 1) {
        // Use the new multiple URLs API
        const generateResponse = await blogsAPI.generateBlogFromUrls({
          urls: validUrls,
          wordCount: wordCount,
        });
        console.log(
          "Blog generation started from multiple URLs:",
          generateResponse,
        );

        // For multiple URLs, we'll poll using the first URL as the identifier
        // (This may need backend adjustment to support multiple URL tracking)
        const generatedBlog = await pollForCompletion(validUrls[0]);
        setResult(generatedBlog);
        console.log(
          "Successfully generated blog from multiple URLs:",
          generatedBlog,
        );
      } else {
        // Single URL - use existing logic
        const singleUrl = validUrls[0];

        // First, check if blog already exists or is in progress
        const initialStatus = await blogsAPI.checkBlogStatus(singleUrl.trim());

        if (initialStatus.status === "completed") {
          // Blog already exists
          setResult(initialStatus.blog);
          console.log("Blog already exists:", initialStatus.blog);
          setLoading(false);
          return;
        }

        // Start generation if not already in progress
        if (initialStatus.status !== "processing") {
          const generateResponse = await blogsAPI.generateBlogFromUrl({
            url: singleUrl.trim(),
            wordCount: wordCount,
          });
          console.log("Blog generation started:", generateResponse);
        }

        // Start polling for completion status
        const generatedBlog = await pollForCompletion(singleUrl.trim());
        setResult(generatedBlog);
        console.log("Successfully generated blog:", generatedBlog);
      }
    } catch (err) {
      console.error("Error generating blog:", err);
      // HTTP errors (403, 401, 500, etc.) are now handled globally by APIService
      // Only handle non-HTTP errors here (network errors, parsing errors, etc.)
      setError(`Failed to generate blog: ${err.message}`);
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  // Enhanced polling with progress updates using status endpoint
  const pollForCompletion = async (url) => {
    const maxAttempts = 18; // 3 minutes at 10-second intervals
    const pollInterval = 10000; // 10 seconds

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Wait before polling (except first attempt)
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        // Check blog status using the enhanced status endpoint
        const status = await blogsAPI.checkBlogStatus(url);

        if (status.status === "completed") {
          // Blog generation is complete
          console.log("Blog generation completed:", status.blog);
          return status.blog;
        } else if (status.status === "processing") {
          // Update progress information
          setProgress({
            startedAt: status.startedAt,
            processingTimeSeconds: status.processingTimeSeconds,
            message: status.message,
          });
          console.log(
            `Polling attempt ${attempt + 1}/${maxAttempts} - Processing for ${
              status.processingTimeSeconds
            }s...`,
          );
        } else if (status.status === "error") {
          // Extract detailed error information and stop polling immediately
          const errorDetails =
            status.errorMessage || status.message || "Blog generation failed";
          const failedAt = status.failedAt
            ? formatDateTime(status.failedAt)
            : "";
          const processingTime = status.processingTimeSeconds || 0;

          const detailedError = `Blog generation failed${
            failedAt ? ` at ${failedAt}` : ""
          } after ${processingTime} seconds. Error: ${errorDetails}`;
          throw new Error(detailedError);
        } else if (status.status === "not_found") {
          // Generation might have failed or not started properly
          if (attempt > 5) {
            // Give it a few attempts before failing
            throw new Error("Blog generation was not started or failed");
          }
        }
      } catch (error) {
        // If the error came from an explicit status check (error/not_found),
        // stop polling immediately instead of swallowing it
        if (
          error.message.includes("Blog generation failed") ||
          error.message.includes("Blog generation was not started")
        ) {
          throw error;
        }
        console.error(`Polling error on attempt ${attempt + 1}:`, error);
        // Continue polling on transient network errors unless it's the last attempt
        if (attempt === maxAttempts - 1) {
          throw error;
        }
      }
    }

    throw new Error("Blog generation timed out after 2 minutes");
  };

  /**
   * Reset form and results
   */
  const handleReset = () => {
    setUrls([""]);
    setWordCount(3000);
    setResult(null);
    setError("");
    setProgress(null);
  };

  return (
    <div className="blog-generator-container">
      <div className="blog-generator-header">
        <h2>Generate Blog Posts</h2>
        <p>
          Enter one or more URLs to generate blog posts using AI-powered content
          generation
        </p>
      </div>

      <form onSubmit={handleSubmit} className="blog-generator-form">
        {/* First URL field only */}
        <div className="form-row">
          <div className="form-group url-group">
            <label htmlFor="url-input-0">URL:</label>
            <input
              id="url-input-0"
              type="url"
              value={urls[0]}
              onChange={(e) => updateUrl(0, e.target.value)}
              placeholder="https://example.com/article"
              className="url-input"
              disabled={loading}
            />
          </div>
        </div>

        {/* Additional URL fields section */}
        {urls.length > 1 && (
          <div className="additional-urls-section">
            <div className="additional-urls-container">
              {urls.slice(1).map((url, index) => (
                <div key={index + 1} className="url-input-row">
                  <input
                    id={`url-input-${index + 1}`}
                    type="url"
                    value={url}
                    onChange={(e) => updateUrl(index + 1, e.target.value)}
                    placeholder="https://example.com/article"
                    className="url-input"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => removeUrlField(index + 1)}
                    className="delete-url-button"
                    disabled={loading}
                    title="Delete this URL"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom row with add source, word count, and action buttons */}
        <div className="bottom-controls-row">
          <button
            type="button"
            onClick={addUrlField}
            className="add-url-button"
            disabled={loading}
          >
            Add additional source
          </button>

          <div className="form-group word-count-group">
            <label htmlFor="word-count-input">
              Word Count: &nbsp; <small>(1000-10000)</small>
            </label>
            <input
              id="word-count-input"
              type="number"
              min="1000"
              max="10000"
              step="1000"
              value={wordCount}
              onChange={(e) => setWordCount(parseInt(e.target.value) || 3000)}
              className="word-count-input"
              disabled={loading}
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="submit-button"
              disabled={loading || !urls.some((url) => url.trim())}
            >
              {loading ? "Generating..." : "Generate"}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="reset-button"
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>
      </form>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      )}

      {/* Loading State with Enhanced Progress */}
      {loading && (
        <div className="loading-message">
          <LoadingSpinner message="Generating blog post from URL... This may take several minutes." />
          {progress && (
            <div className="progress-details">
              <p>
                <strong>Status:</strong> {progress.message}
              </p>
              {progress.processingTimeSeconds && (
                <p>
                  <strong>Processing time:</strong>{" "}
                  {Math.floor(progress.processingTimeSeconds / 60)} minutes{" "}
                  {progress.processingTimeSeconds % 60} seconds
                </p>
              )}
              {progress.startedAt && (
                <p>
                  <strong>Started at:</strong>{" "}
                  {formatTimeOnly(progress.startedAt, true)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Success Result Display */}
      {result && (
        <div className="result-container">
          <h3>Generated Blog Post</h3>

          <div className="blog-preview">
            <div className="blog-metadata">
              <p>
                <strong>Title:</strong> {result.title}
              </p>
              {result.category && (
                <p>
                  <strong>Category:</strong> {result.category}
                </p>
              )}
              {result.author && (
                <p>
                  <strong>Author:</strong> {result.author}
                </p>
              )}
              {result.publishedDate && (
                <p>
                  <strong>Published:</strong> {formatDate(result.publishedDate)}
                </p>
              )}
              {result.id && (
                <p>
                  <strong>Blog ID:</strong> {result.id}
                </p>
              )}
              {result.short_title && (
                <p>
                  <strong>URL Slug:</strong> {result.short_title}
                </p>
              )}
            </div>

            {result.summary && (
              <div className="blog-summary">
                <h4>Summary:</h4>
                <p>{result.summary}</p>
              </div>
            )}

            {result.htmlContent && (
              <div className="blog-content">
                <h4>Content Preview:</h4>
                <div
                  className="content-preview"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      result.htmlContent.substring(0, 500) + "...",
                      {
                        ALLOWED_TAGS: ["p", "br", "strong", "em"],
                        ALLOWED_ATTR: [],
                      },
                    ),
                  }}
                />
              </div>
            )}
          </div>

          <div className="result-actions">
            <button onClick={handleReset} className="generate-another-button">
              Generate Another
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogGenerator;
